import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UploadProgressService, UploadFile, UploadSession } from '../../../services/upload-progress.service';

@Component({
  selector: 'app-upload-progress-popup',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatProgressBarModule,
    MatTooltipModule
  ],
  templateUrl: './upload-progress-popup.component.html',
  styleUrl: './upload-progress-popup.component.css'
})
export class UploadProgressPopupComponent implements OnInit, OnDestroy {
  isMinimized: boolean = false;
  isVisible: boolean = false;
  isDragging: boolean = false;
  
  // Position for dragging
  position = { x: 20, y: 20 };
  private dragOffset = { x: 0, y: 0 };
  
  // Upload state
  currentSession: UploadSession | null = null;
  uploadFiles: UploadFile[] = [];
  overallProgress: number = 0;
  
  private destroy$ = new Subject<void>();

  constructor(private uploadProgressService: UploadProgressService) {}

  ngOnInit(): void {
    // Subscribe to upload session changes
    this.uploadProgressService.currentSession$
      .pipe(takeUntil(this.destroy$))
      .subscribe((session: UploadSession | null) => {
        this.currentSession = session;
        this.isVisible = session !== null && session.files.length > 0;
        
        if (session) {
          this.uploadFiles = session.files;
          this.overallProgress = session.overallProgress;
          
          // Auto-expand when new upload starts
          if (session.status === 'uploading' && this.isMinimized) {
            // Keep minimized if user minimized it manually
          }
        }
      });

    // Subscribe to minimized state
    this.uploadProgressService.isMinimized$
      .pipe(takeUntil(this.destroy$))
      .subscribe((minimized: boolean) => {
        this.isMinimized = minimized;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleMinimize(): void {
    this.isMinimized = !this.isMinimized;
    this.uploadProgressService.setMinimized(this.isMinimized);
  }

  closePopup(): void {
    // Only allow closing if all uploads are complete or failed
    if (this.currentSession?.status === 'uploading') {
      if (!confirm('Uploads are in progress. Are you sure you want to close? This may interrupt the upload.')) {
        return;
      }
      this.uploadProgressService.cancelAllUploads();
    }
    this.uploadProgressService.clearSession();
    this.isVisible = false;
  }

  cancelUpload(file: UploadFile): void {
    this.uploadProgressService.cancelFileUpload(file.id);
  }

  retryUpload(file: UploadFile): void {
    this.uploadProgressService.retryFileUpload(file.id);
  }

  clearCompleted(): void {
    this.uploadProgressService.clearCompletedFiles();
  }

  // Drag functionality
  onDragStart(event: MouseEvent): void {
    if (event.button !== 0) return; // Only left mouse button
    
    this.isDragging = true;
    this.dragOffset = {
      x: event.clientX - this.position.x,
      y: event.clientY - this.position.y
    };
    
    event.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onDragMove(event: MouseEvent): void {
    if (!this.isDragging) return;
    
    this.position = {
      x: Math.max(0, event.clientX - this.dragOffset.x),
      y: Math.max(0, event.clientY - this.dragOffset.y)
    };
  }

  @HostListener('document:mouseup')
  onDragEnd(): void {
    this.isDragging = false;
  }

  // Helper methods
  getStatusIcon(status: UploadFile['status']): string {
    switch (status) {
      case 'pending': return 'schedule';
      case 'uploading': return 'cloud_upload';
      case 'completed': return 'check_circle';
      case 'failed': return 'error';
      case 'cancelled': return 'cancel';
      default: return 'help';
    }
  }

  getStatusClass(status: UploadFile['status']): string {
    return `status-${status}`;
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  getUploadedSize(file: UploadFile): string {
    const uploaded = (file.progress / 100) * file.size;
    return this.formatFileSize(uploaded);
  }

  getCompletedCount(): number {
    return this.uploadFiles.filter(f => f.status === 'completed').length;
  }

  getFailedCount(): number {
    return this.uploadFiles.filter(f => f.status === 'failed').length;
  }

  getUploadingCount(): number {
    return this.uploadFiles.filter(f => f.status === 'uploading').length;
  }

  getPendingCount(): number {
    return this.uploadFiles.filter(f => f.status === 'pending').length;
  }

  getSessionStatusText(): string {
    if (!this.currentSession) return '';
    
    const uploading = this.getUploadingCount();
    const pending = this.getPendingCount();
    const completed = this.getCompletedCount();
    const failed = this.getFailedCount();
    const total = this.uploadFiles.length;
    
    if (this.currentSession.status === 'completed') {
      return `${completed} of ${total} uploaded successfully${failed > 0 ? `, ${failed} failed` : ''}`;
    }
    
    if (this.currentSession.status === 'uploading') {
      return `Uploading ${uploading + pending} of ${total} files...`;
    }
    
    return `${total} files`;
  }

  getFileIcon(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(ext)) {
      return 'image';
    }
    if (['pdf'].includes(ext)) {
      return 'picture_as_pdf';
    }
    if (['doc', 'docx'].includes(ext)) {
      return 'description';
    }
    if (['xls', 'xlsx'].includes(ext)) {
      return 'table_chart';
    }
    if (['txt'].includes(ext)) {
      return 'text_snippet';
    }
    return 'insert_drive_file';
  }

  trackByFileId(index: number, file: UploadFile): string {
    return file.id;
  }

  retryAllFailed(): void {
    this.uploadProgressService.retryAllFailed();
  }

  cancelAllUploads(): void {
    if (confirm('Are you sure you want to cancel all uploads?')) {
      this.uploadProgressService.cancelAllUploads();
    }
  }
}
